import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { rememberHubReturn } from 'utils/hubReturnPath'
import TaskPeopleAvatars from './components/TaskPeopleAvatars'
import SearchableSelect from 'components/SearchableSelect'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import TodayIcon from '@mui/icons-material/Today'
import RepeatIcon from '@mui/icons-material/Repeat'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import EventIcon from '@mui/icons-material/Event'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { SkeletonBlock, SkeletonLine, SkeletonRows } from 'components/Skeleton'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { apiBareUrl } from 'utils/apiUrl'
import { setTaskFiltersRoutine } from './ducks/actions'
import { getRecurringTasks, getTasks } from 'services/taskService'
import { selectTasks, selectTasksLoading, selectTaskFilters } from './ducks/selectors'
import { taskTypeMeta } from './taskType'
import TaskTypeSegment from './components/TaskTypeSegment'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { selectHasAnyTaskManagementBp } from 'features/auth/ducks/selectors'
import { getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import FilterListIcon from '@mui/icons-material/FilterList'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { getFilesWithExpiryDates, getExpiryDateCounts, getCalendarToken, getCalendarForecast, getCalendarRemedials } from 'services/calendarService'
import { fetchDashboardScopeOptions } from 'services/BpService'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import FileTypeMultiSelect from 'features/bpDetails/Components/files/FileTypeMultiSelect'
import { getFileTypesRoutine } from 'ducks/files/actions'
import { getFileCategoriesRoutine } from 'ducks/dictionaries/actions'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import RemedialsDueSection from './components/RemedialsDueSection'
import { REMEDIAL_LEGEND, calendarWindow, dayBadge, forMonth, indexByDate, itemsForDay, readShowRemedials, seeAllUrl, writeShowRemedials } from './calendarRemedials'

const COMPLETED_COLOUR = '#15803d'
const OVERDUE_COLOUR = '#dc2626'

const taskColour = (task) => {
  if (task?.status === 'completed') return COMPLETED_COLOUR
  if (task?.isOverdue && task?.status !== 'completed') return OVERDUE_COLOUR
  return taskTypeMeta(task).color
}

const groupObligationsByName = (items) => {
  const byName = new Map()
  items.forEach(o => {
    if (!byName.has(o.name)) byName.set(o.name, { name: o.name, items: [], earliest: o.date })
    const g = byName.get(o.name)
    g.items.push(o)
    if (moment(o.date).isBefore(moment(g.earliest))) g.earliest = o.date
  })
  return Array.from(byName.values()).sort((a, b) => moment(a.earliest).valueOf() - moment(b.earliest).valueOf())
}

const NARROW_VIEWPORT = '(max-width: 400px)'

const prefersListView = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(NARROW_VIEWPORT).matches
    : false

const TaskCalendar = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  useEffect(() => {
    rememberHubReturn(location.pathname + location.search)
  }, [location.pathname, location.search])
  const tasks = useSelector(selectTasks)
  const isLoading = useSelector(selectTasksLoading)
  const taskFilters = useSelector(selectTaskFilters)
  const typeFilter = taskFilters?.type || null
  const showForecast = !typeFilter || typeFilter === 'obligation'
  const hasTaskManagement = useSelector(selectHasAnyTaskManagementBp)
  const [searchParams, setSearchParams] = useSearchParams()
  const bpFilter = searchParams.get('bp')
  const dateParam = searchParams.get('date')
  const [currentDate, setCurrentDate] = useState(() => {
    const m = searchParams.get('month')
    const parsed = m ? moment(m, 'YYYY-MM', true) : null
    return parsed && parsed.isValid() ? parsed.startOf('month') : moment()
  })
  const [selectedDate, setSelectedDate] = useState(null)
  // On a phone-width screen the month grid can only give each day a ~42px
  // cell, below a comfortable tap target, so the list opens by default there.
  // This only sets the initial view — the toggle still works either way.
  const [viewMode, setViewMode] = useState(() => searchParams.get('view') || (prefersListView() ? 'list' : 'month')) // 'month' or 'list'
  const [expiringFiles, setExpiringFiles] = useState([])
  const [fileMeta, setFileMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 })
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [expiryDateCounts, setExpiryDateCounts] = useState({})
  const [selectedDateFiles, setSelectedDateFiles] = useState([])
  const [loadingDateFiles, setLoadingDateFiles] = useState(false)
  const [taskPage, setTaskPage] = useState(1)
  const ITEMS_PER_PAGE = 15
  const [panelTab, setPanelTab] = useState('tasks')
  const [insightFile, setInsightFile] = useState(null)
  const [showInsightModal, setShowInsightModal] = useState(false)
  const [activeFilter, setActiveFilter] = useState(() => searchParams.get('filter') || null) // 'overdue_tasks' | 'expired_docs' | null
  const [expiredFiles, setExpiredFiles] = useState([])
  const [expiredFileMeta, setExpiredFileMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 })
  const [renewedFiles, setRenewedFiles] = useState([])
  const [renewedFileMeta, setRenewedFileMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 })
  // Hydrate from `?file_type_ids=` so deep links from the portfolio overview
  // (e.g. "View on calendar" for a single file type) auto-select on first load.
  const initialFileTypes = (() => {
    const raw = searchParams.get('file_type_ids')
    return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []
  })()
  const [selectedFileTypes, setSelectedFileTypes] = useState(initialFileTypes)
  const [showFileTypeFilter, setShowFileTypeFilter] = useState(false)
  const fileTypeFilterRef = useRef(null)
  const [showSyncMenu, setShowSyncMenu] = useState(false)
  const [syncUrls, setSyncUrls] = useState(null)
  const [syncScope, setSyncScope] = useState('all')
  const [syncScopeOptions, setSyncScopeOptions] = useState(null)
  const [copied, setCopied] = useState(false)
  const syncRef = useRef(null)
  const [showBpFilter, setShowBpFilter] = useState(false)
  const bpFilterRef = useRef(null)
  const [taskSearch, setTaskSearch] = useState('')
  const [docSearch, setDocSearch] = useState('')
  const [docSearchInput, setDocSearchInput] = useState('')
  const docSearchTimer = useRef(null)

  const handleDocSearchChange = useCallback((value) => {
    setDocSearchInput(value)
    clearTimeout(docSearchTimer.current)
    docSearchTimer.current = setTimeout(() => setDocSearch(value), 350)
  }, [])

  useEffect(() => {
    if (!showSyncMenu) return
    const handleClickOutside = (e) => {
      if (syncRef.current && !syncRef.current.contains(e.target)) {
        setShowSyncMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSyncMenu])

  useEffect(() => {
    if (!showFileTypeFilter) return
    const handleClickOutside = (e) => {
      if (fileTypeFilterRef.current && !fileTypeFilterRef.current.contains(e.target)) {
        setShowFileTypeFilter(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFileTypeFilter])

  useEffect(() => {
    if (!showBpFilter) return
    const handleClickOutside = (e) => {
      if (bpFilterRef.current && !bpFilterRef.current.contains(e.target)) {
        setShowBpFilter(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showBpFilter])

  const userBps = useSelector(getUserBps)

  useEffect(() => {
    if (!userBps.length) {
      dispatch(fetchUserBpsRoutine({ page: 1, sort: '-created_at' }))
    }
    dispatch(getFileTypesRoutine())
    dispatch(getFileCategoriesRoutine())
  }, [dispatch, userBps.length])

  const uniqueBuildings = useMemo(() => {
    const byId = new Map()
    ;(syncScopeOptions?.buildingPassports || []).forEach(bp => {
      if (bp?.id) byId.set(bp.id, { id: bp.id, name: bp.name })
    })
    userBps.forEach(bp => {
      if (bp?.id && bp.status === 'active' && !byId.has(bp.id)) {
        byId.set(bp.id, { id: bp.id, name: bp.name })
      }
    })
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [syncScopeOptions, userBps])

  const fetchExpiringFiles = useCallback((page, monthDate) => {
    setLoadingFiles(true)
    const from = monthDate.clone().startOf('month').format('YYYY-MM-DD')
    const to = monthDate.clone().endOf('month').format('YYYY-MM-DD')
    getFilesWithExpiryDates(page, 25, null, 'all', bpFilter, from, to, selectedFileTypes, false, false, false, docSearch || null)
      .then(res => {
        const newFiles = res.data.data || []
        const meta = res.data.meta || {}
        setExpiringFiles(newFiles)
        setFileMeta({
          currentPage: meta.currentPage || page,
          lastPage: meta.lastPage || 1,
          total: meta.total || 0,
        })
      })
      .catch(err => console.error('Failed to fetch expiring files:', err))
      .finally(() => setLoadingFiles(false))
  }, [bpFilter, selectedFileTypes, docSearch])

  const fetchFilesForDate = useCallback((date) => {
    setLoadingDateFiles(true)
    getFilesWithExpiryDates(1, 100, date, 'all', bpFilter, null, null, selectedFileTypes, false, false, false, docSearch || null)
      .then(res => setSelectedDateFiles(res.data.data || []))
      .catch(err => console.error('Failed to fetch files for date:', err))
      .finally(() => setLoadingDateFiles(false))
  }, [bpFilter, selectedFileTypes, docSearch])

  const fetchExpiredFiles = useCallback((page = 1) => {
    setLoadingFiles(true)
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    getFilesWithExpiryDates(page, 25, null, 'all', bpFilter, null, yesterday, selectedFileTypes, true, false, true, docSearch || null)
      .then(res => {
        setExpiredFiles(res.data.data || [])
        const meta = res.data.meta || {}
        setExpiredFileMeta({
          currentPage: meta.currentPage || page,
          lastPage: meta.lastPage || 1,
          total: meta.total || 0,
        })
      })
      .catch(err => console.error('Failed to fetch expired files:', err))
      .finally(() => setLoadingFiles(false))
  }, [bpFilter, selectedFileTypes, docSearch])

  const fetchRenewedFiles = useCallback((page = 1) => {
    setLoadingFiles(true)
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    // onlyRenewable=true to match the banner pill count (which also filters to renewable types)
    getFilesWithExpiryDates(page, 25, null, 'all', bpFilter, null, yesterday, selectedFileTypes, false, true, true, docSearch || null)
      .then(res => {
        setRenewedFiles(res.data.data || [])
        const meta = res.data.meta || {}
        setRenewedFileMeta({
          currentPage: meta.currentPage || page,
          lastPage: meta.lastPage || 1,
          total: meta.total || 0,
        })
      })
      .catch(err => console.error('Failed to fetch renewed files:', err))
      .finally(() => setLoadingFiles(false))
  }, [bpFilter, selectedFileTypes, docSearch])

  // Fetch upcoming renewals (future-dated renewable files) — same filter as the iCal feed.
  // Used by the "see which events will be added" link in the sync dropdown.
  const [upcomingRenewals, setUpcomingRenewals] = useState([])
  const [upcomingRenewalsMeta, setUpcomingRenewalsMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 })
  const fetchUpcomingRenewals = useCallback((page = 1) => {
    setLoadingFiles(true)
    const today = moment().format('YYYY-MM-DD')
    getFilesWithExpiryDates(page, 25, null, 'all', bpFilter, today, null, selectedFileTypes, false, false, true, docSearch || null)
      .then(res => {
        setUpcomingRenewals(res.data.data || [])
        const meta = res.data.meta || {}
        setUpcomingRenewalsMeta({
          currentPage: meta.currentPage || page,
          lastPage: meta.lastPage || 1,
          total: meta.total || 0,
        })
      })
      .catch(err => console.error('Failed to fetch upcoming renewals:', err))
      .finally(() => setLoadingFiles(false))
  }, [bpFilter, selectedFileTypes, docSearch])

  useEffect(() => {
    // Clear selection when building filter changes
    setSelectedDate(null)
    setSelectedDateFiles([])
    setTaskPage(1)

    if (hasTaskManagement) {
      dispatch(setTaskFiltersRoutine({ buildingId: bpFilter || null }))
    }
    fetchExpiringFiles(1, currentDate)
    getExpiryDateCounts('all', bpFilter, selectedFileTypes)
      .then(res => {
        const counts = {}
        ;(res.data.data || []).forEach(item => {
          counts[item.expiryDate] = item.fileCount
        })
        setExpiryDateCounts(counts)
      })
      .catch(err => console.error('Failed to fetch expiry date counts:', err))
    // Refetch the active filter list so sidebar count stays accurate when bpFilter changes
    if (activeFilter === 'expired_docs') fetchExpiredFiles(1)
    if (activeFilter === 'renewed_docs') fetchRenewedFiles(1)
  }, [dispatch, hasTaskManagement, bpFilter]) // fetchExpiringFiles excluded — month nav calls it directly

  // Refetch when file type filter changes
  useEffect(() => {
    if (!selectedFileTypes) return
    const from = currentDate.clone().startOf('month').format('YYYY-MM-DD')
    const to = currentDate.clone().endOf('month').format('YYYY-MM-DD')
    setLoadingFiles(true)
    getFilesWithExpiryDates(1, 25, null, 'all', bpFilter, from, to, selectedFileTypes)
      .then(res => {
        setExpiringFiles(res.data.data || [])
        const meta = res.data.meta || {}
        setFileMeta({ currentPage: 1, lastPage: meta.lastPage || 1, total: meta.total || 0 })
      })
      .catch(() => {})
      .finally(() => setLoadingFiles(false))
    getExpiryDateCounts('all', bpFilter, selectedFileTypes)
      .then(res => {
        const counts = {}
        ;(res.data.data || []).forEach(item => {
          counts[item.expiryDate] = item.fileCount
        })
        setExpiryDateCounts(counts)
      })
      .catch(() => {})
    // Also refetch the active filter if one is set
    if (activeFilter === 'expired_docs') fetchExpiredFiles(1)
    if (activeFilter === 'renewed_docs') fetchRenewedFiles(1)
  }, [selectedFileTypes]) // only trigger on file type changes

  // Refetch documents when search changes (debounced)
  useEffect(() => {
    if (activeFilter === 'expired_docs') fetchExpiredFiles(1)
    else if (activeFilter === 'renewed_docs') fetchRenewedFiles(1)
    else if (activeFilter === 'upcoming_renewals') fetchUpcomingRenewals(1)
    else if (selectedDate) fetchFilesForDate(selectedDate)
    else fetchExpiringFiles(1, currentDate)
  }, [docSearch])

  // Jump to date from URL param (e.g., from dashboard widget), then clear it
  useEffect(() => {
    if (dateParam) {
      const targetDate = moment(dateParam)
      if (targetDate.isValid()) {
        setCurrentDate(targetDate.clone())
        setSelectedDate(dateParam)
        fetchFilesForDate(dateParam)
      }
      // Clear date from URL so refresh doesn't keep jumping
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('date')
      setSearchParams(newParams, { replace: true })
    }
  }, [])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('date')
    next.set('view', viewMode)
    next.set('month', currentDate.format('YYYY-MM'))
    if (activeFilter) next.set('filter', activeFilter)
    else next.delete('filter')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [viewMode, activeFilter, currentDate, searchParams, setSearchParams])

  const handlePrevMonth = () => {
    setSelectedDate(null)
    setSelectedDateFiles([])
    setActiveFilter(null)
    setCurrentDate(prev => {
      const newDate = prev.clone().subtract(1, 'month')
      fetchExpiringFiles(1, newDate)
      return newDate
    })
  }

  const handleNextMonth = () => {
    setSelectedDate(null)
    setSelectedDateFiles([])
    setActiveFilter(null)
    setCurrentDate(prev => {
      const newDate = prev.clone().add(1, 'month')
      fetchExpiringFiles(1, newDate)
      return newDate
    })
  }

  const handleToday = () => {
    const today = moment()
    const todayStr = today.format('YYYY-MM-DD')
    setCurrentDate(today)
    setSelectedDate(todayStr)
    fetchFilesForDate(todayStr)
    fetchExpiringFiles(1, today)
  }

  const handleTaskClick = (taskId) => {
    redirect(PRIVATE_PATHS.taskDetails.replace(':id', taskId))
  }

  // Invalidate sync URLs when filters change
  useEffect(() => {
    setSyncUrls(null)
    setShowSyncMenu(false)
  }, [bpFilter, selectedFileTypes])

  useEffect(() => {
    fetchDashboardScopeOptions()
      .then(r => setSyncScopeOptions(r?.data?.data || r?.data || null))
      .catch(() => {})
  }, [])

  useEffect(() => {
    setSyncScope(bpFilter ? `bp:${bpFilter}` : 'all')
  }, [bpFilter])

  const syncScopeParams = useMemo(() => {
    const [type, id] = syncScope.split(':')
    if (type === 'portfolio') return { portfolioId: id }
    if (type === 'estate') return { estateId: id }
    if (type === 'bp') return { buildingPassportId: id }
    return {}
  }, [syncScope])

  const syncScopeQuery = useMemo(() => {
    const [type, id] = syncScope.split(':')
    if (type === 'portfolio') return `portfolio_id=${id}`
    if (type === 'estate') return `estate_id=${id}`
    if (type === 'bp') return `bp=${id}`
    return ''
  }, [syncScope])

  const buildSyncUrls = useCallback(async (scopeP, scopeQuery) => {
    try {
      const tokenRes = await getCalendarToken(false, null, selectedFileTypes, null, scopeP)
      const data = tokenRes.data?.data || tokenRes.data
      const token = data?.token
      const parts = []
      if (scopeQuery) parts.push(scopeQuery)
      if (selectedFileTypes.length) parts.push(`file_type_ids=${selectedFileTypes.join(',')}`)
      const queryStr = parts.length ? `?${parts.join('&')}` : ''
      const feedUrl = `${apiBareUrl}/ical/${token}${queryStr}`
      const webcalUrl = feedUrl.replace(/^https?/, 'webcal')
      setSyncUrls({
        feedUrl,
        googleUrl: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`,
        outlookUrl: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(webcalUrl)}`,
        eventCount: data?.eventCount || 0,
        documentCount: data?.documentCount || 0,
        taskCount: data?.taskCount || 0,
        remedialCount: data?.remedialCount || 0,
        earliestDate: data?.earliestDate,
        latestDate: data?.latestDate,
        scopeLabel: data?.scopeLabel,
      })
    } catch (err) {
      console.error('Failed to get calendar token:', err)
    }
  }, [selectedFileTypes])

  const handleSyncClick = () => setShowSyncMenu(v => !v)

  useEffect(() => {
    if (showSyncMenu) buildSyncUrls(syncScopeParams, syncScopeQuery)
  }, [showSyncMenu, syncScopeParams, syncScopeQuery, buildSyncUrls])

  const handleCopyUrl = () => {
    if (syncUrls?.feedUrl) {
      navigator.clipboard.writeText(syncUrls.feedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleFileClick = (file) => {
    setInsightFile(file)
    setShowInsightModal(true)
  }

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const startOfMonth = currentDate.clone().startOf('month')
    const endOfMonth = currentDate.clone().endOf('month')
    const startOfCalendar = startOfMonth.clone().startOf('week')
    const endOfCalendar = endOfMonth.clone().endOf('week')

    const days = []
    let day = startOfCalendar.clone()

    while (day.isSameOrBefore(endOfCalendar)) {
      days.push({
        date: day.clone(),
        isCurrentMonth: day.month() === currentDate.month(),
        isToday: day.isSame(moment(), 'day')
      })
      day.add(1, 'day')
    }

    return days
  }, [currentDate])

  // All recurring tasks are fetched independently of the (capped) task list so
  // the calendar shows every repeat, even ones not loaded into the board.
  const [serverRecurring, setServerRecurring] = useState([])
  useEffect(() => {
    let cancelled = false
    getRecurringTasks()
      .then(res => { if (!cancelled) setServerRecurring(res?.data?.data || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const [rangeTasks, setRangeTasks] = useState([])
  useEffect(() => {
    if (!hasTaskManagement) return undefined
    let cancelled = false
    const from = currentDate.clone().startOf('month').startOf('week').format('YYYY-MM-DD')
    const to = currentDate.clone().endOf('month').endOf('week').format('YYYY-MM-DD')
    const load = async () => {
      const collected = []
      for (let page = 1; page <= 10; page++) {
        const res = await getTasks({
          page,
          limit: 100,
          sort: 'deadline',
          window: 'all',
          'filter[deadline][from]': from,
          'filter[deadline][to]': to,
          ...(bpFilter ? { 'filter[building_passport_id]': bpFilter } : {})
        })
        collected.push(...(res?.data?.data || []))
        const lastPage = res?.data?.meta?.lastPage ?? 1
        if (page >= lastPage) break
      }
      if (!cancelled) setRangeTasks(collected)
    }
    load().catch(() => {})
    return () => { cancelled = true }
  }, [currentDate, bpFilter, hasTaskManagement, tasks])

  const [forecast, setForecast] = useState([])
  useEffect(() => {
    let cancelled = false
    getCalendarForecast(bpFilter || null, 12)
      .then(res => { if (!cancelled) setForecast(res?.data?.data || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [bpFilter])

  const visibleForecast = useMemo(() => (showForecast ? forecast : []), [showForecast, forecast])

  const forecastByDate = useMemo(() => {
    const map = {}
    visibleForecast.forEach(f => {
      if (bpFilter && String(f.bpId) !== bpFilter) return
      const key = moment(f.date).format('YYYY-MM-DD')
      if (!map[key]) map[key] = []
      map[key].push(f)
    })
    return map
  }, [visibleForecast, bpFilter])
  const forecastForDate = (date) => forecastByDate[date.format('YYYY-MM-DD')] || []

  const [showRemedials, setShowRemedials] = useState(readShowRemedials)
  const [remedialFeed, setRemedialFeed] = useState({ items: [], byDate: [], total: 0, loading: false, failed: false })
  const remedialRange = useMemo(() => calendarWindow(currentDate), [currentDate])
  useEffect(() => {
    if (!hasTaskManagement || !showRemedials) {
      setRemedialFeed({ items: [], byDate: [], total: 0, loading: false, failed: false })
      return undefined
    }
    let cancelled = false
    setRemedialFeed(feed => ({ ...feed, loading: true, failed: false }))
    getCalendarRemedials({ ...remedialRange, bp: bpFilter || null })
      .then(res => {
        if (cancelled) return
        const data = res?.data?.data || {}
        setRemedialFeed({ items: data.items || [], byDate: data.byDate || [], total: data.total || 0, loading: false, failed: false })
      })
      .catch(() => { if (!cancelled) setRemedialFeed({ items: [], byDate: [], total: 0, loading: false, failed: true }) })
    return () => { cancelled = true }
  }, [hasTaskManagement, showRemedials, remedialRange, bpFilter])
  const remedialsByDate = useMemo(() => indexByDate(remedialFeed.byDate), [remedialFeed.byDate])
  const remedialsThisMonth = useMemo(() => forMonth(remedialFeed, currentDate.format('YYYY-MM')), [remedialFeed, currentDate])
  const remedialsSelectedDay = useMemo(() => (selectedDate ? itemsForDay(remedialFeed.items, selectedDate) : null), [remedialFeed.items, selectedDate])
  const toggleRemedials = () => setShowRemedials(show => {
    writeShowRemedials(!show)
    return !show
  })
  const remedialSeeAll = () => navigate(seeAllUrl({
    from: selectedDate || currentDate.clone().startOf('month').format('YYYY-MM-DD'),
    to: selectedDate || currentDate.clone().endOf('month').format('YYYY-MM-DD'),
    bp: bpFilter || null,
  }))
  const openRemedial = id => navigate(PRIVATE_PATHS.actionDetail.replace(':id', id))

  const allTasks = useMemo(() => {
    const seen = new Set(tasks.map(t => t.id))
    const merged = [...tasks]
    ;[...rangeTasks, ...serverRecurring].forEach(t => {
      if (t?.id && !seen.has(t.id)) {
        seen.add(t.id)
        merged.push(t)
      }
    })
    return merged
  }, [tasks, rangeTasks, serverRecurring])

  const filteredTasks = useMemo(() => {
    let list = bpFilter ? allTasks.filter(t => String(t.building?.id) === bpFilter) : allTasks
    if (typeFilter) list = list.filter(t => taskTypeMeta(t).key === typeFilter)
    return list
  }, [allTasks, bpFilter, typeFilter])

  // Generate recurring task instances for the calendar view
  const getTaskOccurrences = useMemo(() => {
    const startOfCalendar = currentDate.clone().startOf('month').startOf('week')
    const endOfCalendar = currentDate.clone().endOf('month').endOf('week')
    const occurrences = {}
    const today = moment().startOf('day')

    const realDatesByRoot = {}
    filteredTasks.forEach(t => {
      if (t.parentTaskId && t.deadline) {
        const k = moment(t.deadline).format('YYYY-MM-DD')
        if (!realDatesByRoot[t.parentTaskId]) realDatesByRoot[t.parentTaskId] = new Set()
        realDatesByRoot[t.parentTaskId].add(k)
      }
    })

    filteredTasks.forEach(task => {
      // One-off tasks with deadlines
      if (task.deadline && task.recurrenceType !== 'repeating') {
        const deadlineDate = moment(task.deadline).format('YYYY-MM-DD')
        if (!occurrences[deadlineDate]) occurrences[deadlineDate] = []
        occurrences[deadlineDate].push({
          ...task,
          occurrenceType: 'deadline'
        })
      }

      // Recurring tasks
      if (task.recurrenceType === 'repeating' && task.recurrenceStartDate) {
        const startDate = moment(task.recurrenceStartDate)
        const interval = task.recurrenceInterval || 1
        const unit = task.recurrenceUnit || 'month'
        const endDate = task.recurrenceEndDate ? moment(task.recurrenceEndDate) : null

        // Generate occurrences within the calendar view range (up to 1 year ahead)
        const maxDate = endDate
          ? moment.min(endDate, moment().add(1, 'year'))
          : moment().add(1, 'year')

        // Compute from original start date using multiplication to avoid date drift
        let iterations = 0
        let occurrenceDate = startDate.clone()
        const covered = realDatesByRoot[task.id]
        while (occurrenceDate.isSameOrBefore(endOfCalendar) && occurrenceDate.isSameOrBefore(maxDate)) {
          const dateKey = occurrenceDate.format('YYYY-MM-DD')
          if (occurrenceDate.isSameOrAfter(startOfCalendar) && occurrenceDate.isSameOrAfter(today) && !(covered && covered.has(dateKey))) {
            if (!occurrences[dateKey]) occurrences[dateKey] = []
            occurrences[dateKey].push({
              ...task,
              status: task.status === 'completed' ? 'assigned' : task.status,
              isOverdue: false,
              occurrenceType: 'recurring',
              occurrenceDate: occurrenceDate.clone()
            })
          }
          iterations++
          occurrenceDate = startDate.clone().add(interval * iterations, unit)
        }
      }
    })

    return occurrences
  }, [filteredTasks, currentDate])

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD')
    return getTaskOccurrences[dateKey] || []
  }

  // Get tasks for the currently viewed month
  const monthTasks = useMemo(() => {
    const tasks = []
    const startOfMonth = currentDate.clone().startOf('month')
    const endOfMonth = currentDate.clone().endOf('month')

    let checkDate = startOfMonth.clone()
    while (checkDate.isSameOrBefore(endOfMonth)) {
      const tasksForDay = getTasksForDate(checkDate)
      tasksForDay.forEach(task => {
        tasks.push({
          ...task,
          displayDate: checkDate.clone()
        })
      })
      checkDate.add(1, 'day')
    }

    return tasks
  }, [getTaskOccurrences, currentDate])

  // File count for a date (from lightweight counts endpoint)
  const getFileCountForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD')
    return expiryDateCounts[dateKey] || 0
  }

  // Build map of date → file type names from loaded files
  const filesByDate = useMemo(() => {
    const map = {}
    expiringFiles.forEach(f => {
      const date = f.expirationDate || f.nextInspectionDate
      if (!date) return
      if (!map[date]) map[date] = []
      map[date].push(f.fileType || f.displayName?.substring(0, 20) || 'Document')
    })
    return map
  }, [expiringFiles])

  const getFileLabelsForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD')
    return filesByDate[dateKey] || []
  }

  // All files with expiry dates (upcoming first, then expired)
  const sortedExpiringFiles = useMemo(() => {
    const today = moment().startOf('day')
    return expiringFiles
      .filter(f => f.expirationDate || f.nextInspectionDate)
      .sort((a, b) => {
        const aDate = moment(a.expirationDate || a.nextInspectionDate)
        const bDate = moment(b.expirationDate || b.nextInspectionDate)
        return aDate.diff(bDate)
      })
  }, [expiringFiles])

  // Recurring tasks list
  const recurringTasks = useMemo(() => {
    return filteredTasks.filter(t => t.recurrenceType === 'repeating')
  }, [filteredTasks])

  // Overdue summary counts (across all time, not just current month)
  const overdueTaskCount = useMemo(() => {
    return filteredTasks.filter(t => t.isOverdue && t.status !== 'completed').length
  }, [filteredTasks])

  const [expiredDocCount, setExpiredDocCount] = useState(0)
  const [renewedDocCount, setRenewedDocCount] = useState(0)

  // Fetch expired counts for banner — reacts to BP filter AND file type filter
  useEffect(() => {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    Promise.all([
      getFilesWithExpiryDates(1, 1, null, 'all', bpFilter, null, yesterday, selectedFileTypes, false, false, true),
      getFilesWithExpiryDates(1, 1, null, 'all', bpFilter, null, yesterday, selectedFileTypes, false, true, true),
    ]).then(([unrenewedRes, renewedRes]) => {
      setExpiredDocCount(unrenewedRes.data?.meta?.total || 0)
      setRenewedDocCount(renewedRes.data?.meta?.total || 0)
    }).catch(() => {})
  }, [bpFilter, selectedFileTypes])

  // Overdue tasks list (all, not just current month)
  const overdueTasks = useMemo(() => {
    return filteredTasks
      .filter(t => t.isOverdue && t.status !== 'completed')
      .sort((a, b) => moment(a.deadline || a.recurrenceStartDate).diff(moment(b.deadline || b.recurrenceStartDate)))
  }, [filteredTasks])

  // Selected date tasks
  const selectedDateTasks = useMemo(() => {
    if (!selectedDate) return []
    return getTasksForDate(moment(selectedDate))
  }, [selectedDate, getTaskOccurrences])
  const panelTabsShown = hasTaskManagement && showRemedials && activeFilter !== 'overdue_tasks'
  const activePanelTab = panelTabsShown ? panelTab : 'tasks'
  const panelTaskCount = selectedDate ? selectedDateTasks.length : monthTasks.length
  const panelRemedialCount = selectedDate ? (remedialsByDate[selectedDate]?.total || 0) : remedialsThisMonth.total

  const selectedDateForecast = useMemo(() => {
    if (!selectedDate) return []
    return forecastByDate[selectedDate] || []
  }, [selectedDate, forecastByDate])

  const upcomingObligations = useMemo(() => {
    const byId = new Map()
    visibleForecast.forEach(f => {
      if (bpFilter && String(f.bpId) !== bpFilter) return
      const existing = byId.get(f.obligationId)
      if (!existing || moment(f.date).isBefore(moment(existing.date))) {
        byId.set(f.obligationId, f)
      }
    })
    return Array.from(byId.values()).sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
  }, [visibleForecast, bpFilter])

  const obligationGroups = useMemo(() => groupObligationsByName(upcomingObligations), [upcomingObligations])

  const [obligationsOpen, setObligationsOpen] = useState(false)
  const [openObligationGroups, setOpenObligationGroups] = useState(() => new Set())
  const toggleObligationGroup = (name) => {
    setOpenObligationGroups(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const monthObligations = useMemo(() => {
    const byId = new Map()
    visibleForecast.forEach(f => {
      if (bpFilter && String(f.bpId) !== bpFilter) return
      if (!moment(f.date).isSame(currentDate, 'month')) return
      const existing = byId.get(f.obligationId)
      if (!existing || moment(f.date).isBefore(moment(existing.date))) byId.set(f.obligationId, f)
    })
    return Array.from(byId.values())
  }, [visibleForecast, bpFilter, currentDate])

  const activeTaskList = useMemo(() => {
    const raw = activeFilter === 'overdue_tasks' ? overdueTasks : selectedDate ? selectedDateTasks : monthTasks
    return taskSearch
      ? raw.filter(t => t.title?.toLowerCase().includes(taskSearch.toLowerCase()) || t.building?.name?.toLowerCase().includes(taskSearch.toLowerCase()))
      : raw
  }, [activeFilter, overdueTasks, selectedDate, selectedDateTasks, monthTasks, taskSearch])

  useEffect(() => { setTaskPage(1) }, [selectedDate, taskSearch, activeFilter, currentDate])

  return (
    <PageWrapper>
      <Header>
        <HeaderContent>
          {bpFilter && (
            <BackToBpBtn type='button' onClick={() => navigate(`/bp-list/${bpFilter}`)}>
              ← Back to {uniqueBuildings.find(b => b.id === bpFilter)?.name || 'Building Passport'}
            </BackToBpBtn>
          )}
          <TitleRow>
            <PageTitle>
              <EventIcon aria-hidden='true' />
              <PageHeading>Calendar</PageHeading>
              {bpFilter && (
                <BpFilterChip>
                  <span>{uniqueBuildings.find(b => b.id === bpFilter)?.name || 'Building Passport'}</span>
                  <BpFilterClear onClick={(e) => { e.stopPropagation(); setSearchParams({}) }}>
                    &times;
                  </BpFilterClear>
                </BpFilterChip>
              )}
            </PageTitle>
            <AttentionRow aria-label='Needs attention'>
              {overdueTaskCount > 0 && (
                <AttentionChip
                  $level='critical'
                  $active={activeFilter === 'overdue_tasks'}
                  type='button'
                  aria-pressed={activeFilter === 'overdue_tasks'}
                  title='Show only overdue tasks. Click again to show everything.'
                  onClick={() => {
                    setActiveFilter(prev => prev === 'overdue_tasks' ? null : 'overdue_tasks')
                    setSelectedDate(null)
                    setSelectedDateFiles([])
                  }}
                >
                  {overdueTaskCount} task{overdueTaskCount !== 1 ? 's' : ''} overdue
                </AttentionChip>
              )}
              {expiredDocCount > 0 && (
                <AttentionChip
                  data-tour-id='calendar.renewal-chip'
                  $level='medium'
                  $active={activeFilter === 'expired_docs'}
                  type='button'
                  aria-pressed={activeFilter === 'expired_docs'}
                  title='Show only documents whose expiry date has passed, so they may need renewing. Click again to show everything.'
                  onClick={() => {
                    const newFilter = activeFilter === 'expired_docs' ? null : 'expired_docs'
                    setActiveFilter(newFilter)
                    setSelectedDate(null)
                    setSelectedDateFiles([])
                    if (newFilter === 'expired_docs') fetchExpiredFiles(1)
                  }}
                >
                  {expiredDocCount} file{expiredDocCount !== 1 ? 's' : ''} might need renewal
                </AttentionChip>
              )}
              {renewedDocCount > 0 && (
                <AttentionChip
                  $level='low'
                  $active={activeFilter === 'renewed_docs'}
                  type='button'
                  aria-pressed={activeFilter === 'renewed_docs'}
                  title='Show only documents that have been replaced by a newer version. Click again to show everything.'
                  onClick={() => {
                    const newFilter = activeFilter === 'renewed_docs' ? null : 'renewed_docs'
                    setActiveFilter(newFilter)
                    setSelectedDate(null)
                    setSelectedDateFiles([])
                    if (newFilter === 'renewed_docs') fetchRenewedFiles(1)
                  }}
                >
                  {renewedDocCount} file{renewedDocCount !== 1 ? 's' : ''} renewed
                </AttentionChip>
              )}
            </AttentionRow>
            <HeaderRight>
              <ViewToggle>
                <ToggleButton
                  $active={viewMode === 'month'}
                  onClick={() => setViewMode('month')}
                >
                  Calendar
                </ToggleButton>
                <ToggleButton
                  $active={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
                >
                  List
                </ToggleButton>
              </ViewToggle>
              <SyncWrapper ref={syncRef}>
                <SyncButton onClick={handleSyncClick} data-tour-id='calendar.sync-button'>
                  <AddCircleOutlineIcon fontSize='small' />
                  Add to my calendar
                </SyncButton>
                {showSyncMenu && (
                  <SyncDropdown>
                    <SyncScopeRow data-tour-id='calendar.sync-scope'>
                      <SyncScopeLabel>Scope</SyncScopeLabel>
                      <SearchableSelect
                        value={syncScope}
                        onChange={v => setSyncScope(v)}
                        options={[
                          { value: 'all', label: 'All your Building Passports' },
                          ...(syncScopeOptions?.portfolios || []).map(p => ({ value: `portfolio:${p.id}`, label: p.name, group: 'Portfolios' })),
                          ...(syncScopeOptions?.estates || []).map(es => ({ value: `estate:${es.id}`, label: es.name, group: 'Estates' })),
                          ...(syncScopeOptions?.buildingPassports || []).map(b => ({ value: `bp:${b.id}`, label: b.name, group: 'Building Passports' })),
                        ]}
                        searchPlaceholder='Search…'
                        ariaLabel='Calendar sync scope'
                      />
                    </SyncScopeRow>
                    <SyncNote>
                      <strong>Live calendar subscription: renewals, task and remedial due dates</strong>
                      <br /><br />
                      {syncUrls ? (
                        <>
                          <SyncStatsClickable
                            onClick={() => {
                              setActiveFilter('upcoming_renewals')
                              setSelectedDate(null)
                              setSelectedDateFiles([])
                              fetchUpcomingRenewals(1)
                              setShowSyncMenu(false)
                            }}
                            title="Click to see the list of events"
                          >
                            {syncUrls.eventCount} upcoming event{syncUrls.eventCount !== 1 ? 's' : ''} will appear in your calendar →
                          </SyncStatsClickable>
                          <SyncBreakdown>
                            <SyncBreakdownItem $muted>
                              {syncUrls.documentCount} document date{syncUrls.documentCount !== 1 ? 's' : ''} · {syncUrls.taskCount} task due date{syncUrls.taskCount !== 1 ? 's' : ''} · {syncUrls.remedialCount} remedial due date{syncUrls.remedialCount !== 1 ? 's' : ''}
                            </SyncBreakdownItem>
                          </SyncBreakdown>
                          {syncUrls.scopeLabel && (
                            <SyncBreakdown>
                              <SyncBreakdownItem $muted>Scope: {syncUrls.scopeLabel}</SyncBreakdownItem>
                            </SyncBreakdown>
                          )}
                        </>
                      ) : (
                        <SyncBreakdown><SyncBreakdownItem $muted>Preparing subscription…</SyncBreakdownItem></SyncBreakdown>
                      )}
                      <br />
                      <strong>Includes:</strong> document renewal and inspection dates, task due dates and the due dates of remedials with no task yet, for the scope above, only for buildings you can access.
                      <br /><br />
                      <em>Google Calendar and Outlook fetch subscribed calendars on their own schedule, so events usually appear within a few hours, but the first sync can take up to 24 hours.</em>
                    </SyncNote>
                    {syncUrls && (
                      <>
                        <SyncDivider />
                        <SyncOption onClick={() => { window.open(syncUrls.googleUrl, '_blank'); setShowSyncMenu(false) }}>
                          Subscribe in Google Calendar
                        </SyncOption>
                        <SyncOption onClick={() => { window.open(syncUrls.outlookUrl, '_blank'); setShowSyncMenu(false) }}>
                          Subscribe in Outlook
                        </SyncOption>
                        <SyncDivider />
                        <SyncOption onClick={handleCopyUrl}>
                          <ContentCopyIcon fontSize='small' style={{ marginRight: 8 }} />
                          {copied ? 'Copied!' : 'Copy subscription URL'}
                        </SyncOption>
                      </>
                    )}
                    <SyncDivider />
                    <SyncOption onClick={() => { navigate(PRIVATE_PATHS.calendarSubscribe); setShowSyncMenu(false) }}>
                      Open full subscription page →
                    </SyncOption>
                  </SyncDropdown>
                )}
              </SyncWrapper>
            </HeaderRight>
          </TitleRow>
          <FilterRow>
            {hasTaskManagement && <TaskTypeSegment />}
            {hasTaskManagement && (
              <RemedialToggle
                type='button'
                aria-pressed={showRemedials}
                onClick={toggleRemedials}
                title={showRemedials ? 'Hide remedial due dates on the calendar' : 'Show remedial due dates on the calendar'}
              >
                {showRemedials ? <VisibilityOutlinedIcon fontSize='small' aria-hidden='true' /> : <VisibilityOffOutlinedIcon fontSize='small' aria-hidden='true' />}
                {showRemedials ? 'Remedials shown' : 'Remedials hidden'}
              </RemedialToggle>
            )}
            <FileTypeFilterWrapper ref={fileTypeFilterRef}>
              <SyncButton onClick={() => setShowFileTypeFilter(!showFileTypeFilter)} $hasSelection={selectedFileTypes.length > 0}>
                <FilterListIcon fontSize='small' />
                {selectedFileTypes.length ? `${selectedFileTypes.length} type${selectedFileTypes.length > 1 ? 's' : ''} filtered` : 'File Types'}
                {selectedFileTypes.length > 0 && (
                  <ClearFilterInline onClick={(e) => { e.stopPropagation(); setSelectedFileTypes([]); setShowFileTypeFilter(false) }}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </ClearFilterInline>
                )}
              </SyncButton>
              {showFileTypeFilter && (
                <FileTypeDropdown>
                  <FileTypeMultiSelect
                    selectedFileTypes={selectedFileTypes}
                    setSelectedFileTypes={setSelectedFileTypes}
                    autoOpen
                  />
                </FileTypeDropdown>
              )}
            </FileTypeFilterWrapper>
            {uniqueBuildings.length > 0 && (
              <SyncWrapper ref={bpFilterRef}>
                <SyncButton onClick={() => setShowBpFilter(!showBpFilter)}>
                  <FilterListIcon fontSize='small' />
                  {bpFilter ? uniqueBuildings.find(b => b.id === bpFilter)?.name?.substring(0, 20) || 'Building Passport' : 'All Building Passports'}
                </SyncButton>
                {showBpFilter && (
                  <SyncDropdown>
                    <SyncOption onClick={() => { setSearchParams({}); setShowBpFilter(false) }}>
                      All Building Passports
                    </SyncOption>
                    <SyncDivider />
                    {uniqueBuildings.map(bp => (
                      <SyncOption key={bp.id} onClick={() => { setSearchParams({ bp: bp.id }); setShowBpFilter(false) }}>
                        {bp.name}
                      </SyncOption>
                    ))}
                  </SyncDropdown>
                )}
              </SyncWrapper>
            )}
          </FilterRow>
        </HeaderContent>
      </Header>

      <ContentArea>
        {isLoading && !expiringFiles.length && !Object.keys(expiryDateCounts).length ? (
          <CalendarLayout $hasTaskManagement={hasTaskManagement} aria-busy='true' aria-label='Loading the calendar'>
            <SkeletonCard>
              <SkeletonLine $w='160px' $h={20} />
              <SkeletonGrid>
                {Array.from({ length: 35 }).map((_, i) => <SkeletonBlock key={i} $h={64} $r='8px' />)}
              </SkeletonGrid>
            </SkeletonCard>
            {[0, 1].slice(0, hasTaskManagement ? 2 : 1).map(i => (
              <SkeletonCard key={i}>
                <SkeletonLine $w='60%' $h={16} />
                <SkeletonRows rows={6} gap={18} widths={['100%']} />
              </SkeletonCard>
            ))}
          </CalendarLayout>
        ) : viewMode === 'month' ? (
          <CalendarLayout $hasTaskManagement={hasTaskManagement}>
            <CalendarMain>
              <CalendarHeader>
                <MonthNav>
                  <NavButton onClick={handlePrevMonth}>
                    <ChevronLeftIcon />
                  </NavButton>
                  <MonthTitle>{currentDate.format('MMMM YYYY')}</MonthTitle>
                  <NavButton onClick={handleNextMonth}>
                    <ChevronRightIcon />
                  </NavButton>
                </MonthNav>
                <CalendarActions>
                  {selectedDate && (
                    <ClearButton onClick={() => { setSelectedDate(null); setSelectedDateFiles([]) }}>
                      <CloseIcon style={{ fontSize: 14 }} />
                      Clear {moment(selectedDate).format('D MMM')}
                    </ClearButton>
                  )}
                  <TodayButton onClick={handleToday} data-tour-id='calendar.today-btn'>
                    <TodayIcon />
                    Today
                  </TodayButton>
                </CalendarActions>
              </CalendarHeader>

              <CalendarGrid data-tour-id='calendar.grid'>
                <WeekdayHeader>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <WeekdayCell key={day}>{day}</WeekdayCell>
                  ))}
                </WeekdayHeader>

                <DaysGrid>
                  {calendarDays.map((day, index) => {
                    const tasksForDay = hasTaskManagement ? getTasksForDate(day.date) : []
                    const forecastForDay = hasTaskManagement ? forecastForDate(day.date) : []
                    const fileCount = getFileCountForDate(day.date)
                    const fileLabels = getFileLabelsForDate(day.date)
                    const isSelected = selectedDate === day.date.format('YYYY-MM-DD')
                    const completedCount = tasksForDay.filter(t => t.status === 'completed').length
                    const overdueCount = tasksForDay.filter(t => t.isOverdue && t.status !== 'completed').length
                    const upcomingTasks = tasksForDay.filter(t => t.status !== 'completed' && !t.isOverdue)
                    const upObligation = upcomingTasks.filter(t => taskTypeMeta(t).key === 'obligation').length
                    const upRecurring = upcomingTasks.filter(t => taskTypeMeta(t).key === 'recurring').length
                    const upOneOff = upcomingTasks.filter(t => taskTypeMeta(t).key === 'one_off').length
                    const hasOverdue = overdueCount > 0
                    const filesOverdue = fileCount > 0 && day.date.isBefore(moment().startOf('day'))
                    const remedialBadge = hasTaskManagement && showRemedials ? dayBadge(remedialsByDate[day.date.format('YYYY-MM-DD')]) : null
                    const totalItems = tasksForDay.length + fileCount + forecastForDay.length + (remedialBadge?.count || 0)

                    return (
                      <DayCell
                        key={index}
                        $isCurrentMonth={day.isCurrentMonth}
                        $isToday={day.isToday}
                        $isSelected={isSelected}
                        $hasOverdue={hasOverdue}
                        onClick={() => {
                          const dateStr = day.date.format('YYYY-MM-DD')
                          if (selectedDate === dateStr) {
                            setSelectedDate(null)
                            setSelectedDateFiles([])
                          } else {
                            setSelectedDate(dateStr)
                            setActiveFilter(null)
                            fetchFilesForDate(dateStr)
                          }
                        }}
                      >
                        <DayNumber $isToday={day.isToday}>{day.date.date()}</DayNumber>
                        {totalItems > 0 && (
                          <TaskIndicators>
                            {overdueCount > 0 && (
                              <DayBadge $color={OVERDUE_COLOUR} title={`${overdueCount} overdue task${overdueCount > 1 ? 's' : ''}, not yet completed`}>
                                {overdueCount} overdue
                              </DayBadge>
                            )}
                            {completedCount > 0 && (
                              <DayBadge $color={COMPLETED_COLOUR} title={`${completedCount} completed task${completedCount > 1 ? 's' : ''}`}>
                                {completedCount} done
                              </DayBadge>
                            )}
                            {upObligation > 0 && (
                              <DayBadge $color="#0e7490" title={`${upObligation} upcoming obligation task${upObligation > 1 ? 's' : ''}`}>
                                {upObligation} obligation{upObligation > 1 ? 's' : ''}
                              </DayBadge>
                            )}
                            {upRecurring > 0 && (
                              <DayBadge $color="#7c3aed" title={`${upRecurring} upcoming recurring task${upRecurring > 1 ? 's' : ''}`}>
                                {upRecurring} recurring
                              </DayBadge>
                            )}
                            {upOneOff > 0 && (
                              <DayBadge $color="#64748b" title={`${upOneOff} upcoming one-off task${upOneOff > 1 ? 's' : ''}`}>
                                {upOneOff} one-off
                              </DayBadge>
                            )}
                            {remedialBadge && (
                              <DayBadge $color={remedialBadge.color} title={remedialBadge.title}>
                                {remedialBadge.label}
                              </DayBadge>
                            )}
                            {fileCount > 0 && (
                              <DayBadge
                                $color={filesOverdue ? '#ca8a04' : '#0891b2'}
                                title={fileLabels.length > 0
                                  ? `${filesOverdue ? 'Expired' : 'Due'}: ${fileLabels.join(', ')}`
                                  : `${fileCount} document${fileCount > 1 ? 's' : ''} ${filesOverdue ? 'expired' : 'due'}`}
                              >
                                {filesOverdue ? '⚠ ' : ''}{fileCount} {fileCount === 1 ? 'doc' : 'docs'}
                              </DayBadge>
                            )}
                            {forecastForDay.length > 0 && (
                              <ForecastBadge title={`Upcoming (forecast): ${forecastForDay.map(f => f.name).join(', ')}`}>
                                ↺ {forecastForDay.length}
                              </ForecastBadge>
                            )}
                          </TaskIndicators>
                        )}
                      </DayCell>
                    )
                  })}
                </DaysGrid>
              </CalendarGrid>

              <Legend>
                {hasTaskManagement && (
                  <>
                    <LegendItem>
                      <TaskDot $color={COMPLETED_COLOUR} />
                      <span>Completed</span>
                    </LegendItem>
                    <LegendItem>
                      <TaskDot $color={OVERDUE_COLOUR} />
                      <span>Overdue</span>
                    </LegendItem>
                    <LegendItem>
                      <TaskDot $color="#64748b" />
                      <span>Upcoming one-off task</span>
                    </LegendItem>
                    <LegendItem>
                      <TaskDot $color="#7c3aed" />
                      <span>Upcoming recurring task</span>
                    </LegendItem>
                    <LegendItem>
                      <TaskDot $color="#0e7490" />
                      <span>Upcoming obligation</span>
                    </LegendItem>
                    <LegendItem>
                      <ForecastDot />
                      <span>Upcoming obligation (forecast)</span>
                    </LegendItem>
                    {showRemedials && REMEDIAL_LEGEND.map(item => (
                      <LegendItem key={item.level}>
                        <TaskDot $color={item.color} />
                        <span>{item.label}</span>
                      </LegendItem>
                    ))}
                  </>
                )}
                <LegendItem>
                  <TaskDot $color="#0891b2" />
                  <span>Document due for renewal</span>
                </LegendItem>
                <LegendItem>
                  <TaskDot $color="#ca8a04" />
                  <span>Document expired</span>
                </LegendItem>
              </Legend>
            </CalendarMain>

            {hasTaskManagement && (
              <Panel>
                <SidebarHeader>
                  <SidebarTitle>
                    {activeFilter === 'overdue_tasks' ? 'Overdue tasks' : selectedDate ? moment(selectedDate).format('dddd, D MMM YYYY') : currentDate.format('MMMM YYYY')}
                  </SidebarTitle>
                  {!panelTabsShown && (
                    <TaskCount>
                      {activeFilter === 'overdue_tasks'
                        ? `${overdueTasks.length} task${overdueTasks.length !== 1 ? 's' : ''}`
                        : `${panelTaskCount} task${panelTaskCount !== 1 ? 's' : ''}`}
                    </TaskCount>
                  )}
                </SidebarHeader>
                {panelTabsShown && (
                  <PanelTabs role='tablist' aria-label='What to show'>
                    <PanelTab type='button' role='tab' aria-selected={activePanelTab === 'tasks'} onClick={() => setPanelTab('tasks')}>
                      <AssignmentIcon aria-hidden='true' />
                      <span>Tasks</span>
                      <PanelTabCount>{panelTaskCount}</PanelTabCount>
                    </PanelTab>
                    <PanelTab type='button' role='tab' aria-selected={activePanelTab === 'remedials'} onClick={() => setPanelTab('remedials')}>
                      <BoltOutlinedIcon aria-hidden='true' />
                      <span>Remedials</span>
                      {!remedialFeed.loading && !remedialFeed.failed && <PanelTabCount>{panelRemedialCount}</PanelTabCount>}
                    </PanelTab>
                  </PanelTabs>
                )}
                <PanelSearchBar>
                  <SearchIcon style={{ fontSize: 16, color: '#9ca3af' }} />
                  <PanelSearchInput
                    placeholder={activePanelTab === 'remedials' ? 'Search remedials by title or building...' : 'Search tasks and obligations...'}
                    aria-label={activePanelTab === 'remedials' ? 'Search remedials' : 'Search tasks and obligations'}
                    value={taskSearch}
                    onChange={e => setTaskSearch(e.target.value)}
                  />
                  {taskSearch && <PanelSearchClear onClick={() => setTaskSearch('')}>&times;</PanelSearchClear>}
                </PanelSearchBar>
                {activePanelTab === 'remedials' ? (
                  <TaskList>
                    <RemedialsDueSection
                      compact
                      heading={selectedDate ? 'Remedials due this day' : `Remedials due in ${currentDate.format('MMMM')}`}
                      items={remedialsSelectedDay || remedialsThisMonth.items}
                      total={panelRemedialCount}
                      loading={remedialFeed.loading}
                      failed={remedialFeed.failed}
                      search={taskSearch}
                      onOpen={openRemedial}
                      onSeeAll={remedialSeeAll}
                    />
                  </TaskList>
                ) : (
                <>
                <TaskList>
                  {(() => {
                    const items = activeTaskList
                    const paged = activeFilter === 'overdue_tasks' ? items : items.slice(0, taskPage * ITEMS_PER_PAGE)
                    if (paged.length === 0) return (
                      <EmptyMessage>{activeFilter === 'overdue_tasks' ? 'No overdue tasks' : selectedDate ? 'No tasks on this day' : 'No tasks this month'}</EmptyMessage>
                    )
                    return paged.map((task, i) => {
                      const displayStatus = task.isOverdue && task.status !== 'completed' ? 'overdue' : task.status
                      const statusInfo = taskStatusMeta(displayStatus)
                      return (
                        <TaskItem key={`t-${i}`} $domainColour={task.taskDomain?.colour} onClick={() => handleTaskClick(task.id)}>
                          <TaskIcon $color={taskColour(task)}>
                            {task.occurrenceType === 'recurring' ? <RepeatIcon /> : <AssignmentIcon />}
                          </TaskIcon>
                          <TaskInfo>
                            <TaskTitle>{task.title}</TaskTitle>
                            <TaskMeta>
                              {!selectedDate && (task.displayDate || task.deadline) && (
                                <TaskDate $overdue={task.isOverdue}>
                                  {(task.displayDate || moment(task.deadline || task.recurrenceStartDate)).format('MMM D, YYYY')}
                                </TaskDate>
                              )}
                              <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
                                {statusInfo.label}
                              </StatusBadge>
                              {task.taskDomain && (
                                <DomainChip $colour={task.taskDomain.colour}>
                                  <DomainSwatch $colour={task.taskDomain.colour} />
                                  <span>{task.taskDomain.short || task.taskDomain.name}</span>
                                </DomainChip>
                              )}
                              {task.building && <TaskBuilding>{task.building.name}</TaskBuilding>}
                              {task.team && <TaskTeam>{task.team.name}</TaskTeam>}
                            </TaskMeta>
                          </TaskInfo>
                        </TaskItem>
                      )
                    })
                  })()}
                  {activeFilter !== 'overdue_tasks' && (
                    <InfiniteScrollSentinel hasMore={taskPage * ITEMS_PER_PAGE < activeTaskList.length} loading={false} onLoadMore={() => setTaskPage(p => p + 1)} />
                  )}
                </TaskList>
                {activeFilter !== 'overdue_tasks' && activeTaskList.length > 0 && (
                  <ListCountLine>Showing {Math.min(taskPage * ITEMS_PER_PAGE, activeTaskList.length)} of {activeTaskList.length} tasks</ListCountLine>
                )}
                {activeFilter !== 'overdue_tasks' && (() => {
                  const raw = selectedDate ? selectedDateForecast : monthObligations
                  const source = taskSearch
                    ? raw.filter(o => o.name?.toLowerCase().includes(taskSearch.toLowerCase()) || o.bpName?.toLowerCase().includes(taskSearch.toLowerCase()))
                    : raw
                  if (source.length === 0) return null
                  const groups = groupObligationsByName(source)
                  const sectionOpen = selectedDate ? true : obligationsOpen
                  return (
                    <ObligationSection>
                      <SectionLabel
                        $clickable={!selectedDate}
                        onClick={selectedDate ? undefined : () => setObligationsOpen(o => !o)}
                      >
                        <AutorenewIcon style={{ fontSize: 15 }} />
                        <span>Upcoming obligations</span>
                        <SectionCount>{source.length}</SectionCount>
                        {!selectedDate && (
                          <ChevronRightIcon style={{ fontSize: 18, color: '#94a3b8', transform: obligationsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                        )}
                      </SectionLabel>
                      {sectionOpen && (
                        <>
                          <SectionHint>A forecast of what falls due. Each task is created automatically about two weeks before its due date, once the previous one has been completed.</SectionHint>
                          <ObligationList>
                            {groups.flatMap(g => {
                              const groupOpen = openObligationGroups.has(g.name)
                              const rows = [
                                <GroupRow key={`dg-${g.name}`} onClick={() => toggleObligationGroup(g.name)}>
                                  <GroupName>{g.name}</GroupName>
                                  <GroupMeta>
                                    <ForecastTag>Forecast</ForecastTag>
                                    <GroupCount>{g.items.length}</GroupCount>
                                    <ChevronRightIcon style={{ fontSize: 18, color: '#94a3b8', transform: groupOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                  </GroupMeta>
                                </GroupRow>,
                              ]
                              if (groupOpen) {
                                g.items.forEach((o, i) => rows.push(
                                  <SubRow
                                    key={`ds-${g.name}-${o.obligationId}-${i}`}
                                    onClick={o.bpId ? () => redirect(PRIVATE_PATHS.compliance.replace(':bpId', o.bpId)) : undefined}
                                  >
                                    <SubName>{o.bpName || 'Building Passport'}</SubName>
                                    {!selectedDate && o.date && <SubDate>{moment(o.date).format('MMM D')}</SubDate>}
                                  </SubRow>
                                ))
                              }
                              return rows
                            })}
                          </ObligationList>
                        </>
                      )}
                    </ObligationSection>
                  )
                })()}
                </>
                )}
              </Panel>
            )}

              <Panel $wide={!hasTaskManagement}>
                <SidebarHeader>
                  <SidebarTitle>
                    {activeFilter === 'expired_docs' ? 'Documents that may need renewal' : activeFilter === 'renewed_docs' ? 'Renewed documents' : activeFilter === 'upcoming_renewals' ? 'Upcoming renewals (calendar preview)' : selectedDate ? moment(selectedDate).format('dddd, D MMM YYYY') : `Documents in ${currentDate.format('MMMM YYYY')}`}
                  </SidebarTitle>
                  <TaskCount>
                    {activeFilter === 'expired_docs'
                      ? `${expiredFileMeta.total} file${expiredFileMeta.total !== 1 ? 's' : ''}`
                      : activeFilter === 'renewed_docs'
                        ? `${renewedFileMeta.total} file${renewedFileMeta.total !== 1 ? 's' : ''}`
                        : activeFilter === 'upcoming_renewals'
                          ? `${upcomingRenewalsMeta.total} file${upcomingRenewalsMeta.total !== 1 ? 's' : ''}`
                          : selectedDate
                            ? `${selectedDateFiles.length} file${selectedDateFiles.length !== 1 ? 's' : ''}`
                            : `${fileMeta.total} file${fileMeta.total !== 1 ? 's' : ''}`}
                  </TaskCount>
                </SidebarHeader>
                <PanelSearchBar>
                  <SearchIcon style={{ fontSize: 16, color: '#9ca3af' }} />
                  <PanelSearchInput
                    placeholder="Search documents..."
                    value={docSearchInput}
                    onChange={e => handleDocSearchChange(e.target.value)}
                  />
                  {docSearchInput && <PanelSearchClear onClick={() => { handleDocSearchChange(''); setDocSearch('') }}>&times;</PanelSearchClear>}
                </PanelSearchBar>
                <TaskList>
                  {(loadingFiles || loadingDateFiles) ? (
                    <PanelSkeleton>
                      <SkeletonRows rows={5} gap={18} widths={['100%']} />
                    </PanelSkeleton>
                  ) : (() => {
                    const items = activeFilter === 'expired_docs' ? expiredFiles : activeFilter === 'renewed_docs' ? renewedFiles : activeFilter === 'upcoming_renewals' ? upcomingRenewals : selectedDate ? selectedDateFiles : sortedExpiringFiles
                    if (items.length === 0) return (
                      <EmptyMessage>{activeFilter === 'expired_docs' ? 'All documents are up to date' : activeFilter === 'renewed_docs' ? 'No renewed documents' : activeFilter === 'upcoming_renewals' ? 'No upcoming renewals' : selectedDate ? 'No documents on this day' : 'No documents this month'}</EmptyMessage>
                    )
                    return items.map((file, i) => {
                      const isOverdue = file.expirationDate && moment(file.expirationDate).isBefore(moment())
                      const needsAction = isOverdue && !file.isRenewed
                      const daysOverdue = isOverdue ? moment().diff(moment(file.expirationDate), 'days') : 0
                      return (
                        <FileCard key={`f-${i}`} onClick={() => handleFileClick(file)} $renewed={file.isRenewed && isOverdue}>
                          {needsAction && <FileCardUrgentBar />}
                          <FileThumbnail>
                            {file.thumbnailUrl ? (
                              <img src={file.thumbnailUrl} alt="" />
                            ) : (
                              <InsertDriveFileIcon style={{ color: '#9ca3af', fontSize: 28 }} />
                            )}
                          </FileThumbnail>
                          <FileCardContent>
                            <FileCardName $renewed={file.isRenewed && isOverdue}>{file.displayName}</FileCardName>
                            {file.fileType && file.fileType !== 'Unknown' && (
                              <FileCardType>
                                {file.fileType}
                                {file.renewalPeriod && <span style={{ color: '#7c3aed', marginLeft: 4 }}>({file.renewalPeriod})</span>}
                              </FileCardType>
                            )}

                            {needsAction && (
                              <FileStatusMessage $type={file.expiryDateSource === 'calculated' ? 'warning' : 'urgent'}>
                                {file.expiryDateSource === 'calculated'
                                  ? `May need renewal. The expiry date was estimated (${daysOverdue > 365 ? `${Math.floor(daysOverdue / 365)} year${Math.floor(daysOverdue / 365) > 1 ? 's' : ''} ago` : `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} ago`}). No renewed version found.`
                                  : `Expired ${daysOverdue > 365 ? `${Math.floor(daysOverdue / 365)} year${Math.floor(daysOverdue / 365) > 1 ? 's' : ''} ago` : `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} ago`}. No renewed version has been uploaded.`
                                }
                              </FileStatusMessage>
                            )}
                            {file.isRenewed && isOverdue && (
                              <FileStatusMessage $type="ok">
                                A newer version of this document has been uploaded. No action required.
                              </FileStatusMessage>
                            )}
                            {!isOverdue && file.expirationDate && (
                              <FileStatusMessage $type="info">
                                {file.expiryDateSource === 'aiExtracted'
                                  ? `Expires ${moment(file.expirationDate).format('D MMM YYYY')} (date found in document)`
                                  : file.expiryDateSource === 'calculated'
                                    ? `Expires ${moment(file.expirationDate).format('D MMM YYYY')} (estimated from ${file.renewalPeriod || 'document type'} renewal cycle)`
                                    : file.expiryDateSource === 'user'
                                      ? `Expires ${moment(file.expirationDate).format('D MMM YYYY')} (manually entered)`
                                      : `Expires ${moment(file.expirationDate).format('D MMM YYYY')}`}
                              </FileStatusMessage>
                            )}

                            {file.hasUnresolvedFlags && (
                              <FlagWarning title="This document has validation notes. Open Guru Insight for details.">
                                <WarningAmberIcon style={{ fontSize: 13, color: '#d97706' }} /> Validation notes
                              </FlagWarning>
                            )}
                            {file.buildingPassportName && (
                              <FileCardBuilding>{file.buildingPassportName}</FileCardBuilding>
                            )}
                          </FileCardContent>
                        </FileCard>
                      )
                    })
                  })()}
                </TaskList>
                {!selectedDate && (() => {
                  const meta = activeFilter === 'expired_docs' ? expiredFileMeta : activeFilter === 'renewed_docs' ? renewedFileMeta : activeFilter === 'upcoming_renewals' ? upcomingRenewalsMeta : fileMeta
                  const fetchFn = activeFilter === 'expired_docs' ? fetchExpiredFiles : activeFilter === 'renewed_docs' ? fetchRenewedFiles : activeFilter === 'upcoming_renewals' ? fetchUpcomingRenewals : (p) => fetchExpiringFiles(p, currentDate)
                  if (meta.lastPage <= 1) return null
                  return (
                    <PaginationFooter>
                      <PaginationButton disabled={meta.currentPage <= 1 || loadingFiles} onClick={() => fetchFn(1)}>
                        <FirstPageIcon />
                      </PaginationButton>
                      <PaginationButton disabled={meta.currentPage <= 1 || loadingFiles} onClick={() => fetchFn(meta.currentPage - 1)}>
                        <ChevronLeftIcon />
                      </PaginationButton>
                      <PaginationInfo>
                        {loadingFiles ? '...' : `${meta.currentPage} / ${meta.lastPage}`}
                      </PaginationInfo>
                      <PaginationButton disabled={meta.currentPage >= meta.lastPage || loadingFiles} onClick={() => fetchFn(meta.currentPage + 1)}>
                        <ChevronRightIcon />
                      </PaginationButton>
                      <PaginationButton disabled={meta.currentPage >= meta.lastPage || loadingFiles} onClick={() => fetchFn(meta.lastPage)}>
                        <LastPageIcon />
                      </PaginationButton>
                    </PaginationFooter>
                  )
                })()}
            </Panel>
          </CalendarLayout>
        ) : (
          <ListView>
            {hasTaskManagement && (
              <ListSection>
                <ListSectionHeader>
                  <RepeatIcon />
                  Recurring Tasks
                  <ListSectionCount>{recurringTasks.length}</ListSectionCount>
                </ListSectionHeader>
                {recurringTasks.length === 0 ? (
                  <EmptyMessage>No recurring tasks scheduled</EmptyMessage>
                ) : (
                  recurringTasks.map(task => {
                    const displayStatus = task.isOverdue && task.status !== 'completed' ? 'overdue' : task.status
                    const statusInfo = taskStatusMeta(displayStatus)
                    return (
                      <ListTaskItem key={task.id} onClick={() => handleTaskClick(task.id)}>
                        <ListTaskIcon>
                          <RepeatIcon />
                        </ListTaskIcon>
                        <ListTaskInfo>
                          <ListTaskTitle>{task.title}</ListTaskTitle>
                          <ListTaskMeta>
                            <RecurrenceInfo>
                              Every {task.recurrenceInterval} {task.recurrenceUnit}(s)
                              {task.recurrenceStartDate && ` starting ${moment(task.recurrenceStartDate).format('MMM D, YYYY')}`}
                            </RecurrenceInfo>
                          </ListTaskMeta>
                          <ListTaskMeta>
                            <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
                              {statusInfo.label}
                            </StatusBadge>
                            {task.building && <TaskBuilding>{task.building.name}</TaskBuilding>}
                            {task.team && <TaskTeam>{task.team.name}</TaskTeam>}
                          </ListTaskMeta>
                        </ListTaskInfo>
                        <TaskPeopleAvatars task={task} max={3} />
                      </ListTaskItem>
                    )
                  })
                )}
              </ListSection>
            )}
            {hasTaskManagement && obligationGroups.length > 0 && (
              <ListSection>
                <ClickableHeader onClick={() => setObligationsOpen(o => !o)}>
                  <AutorenewIcon />
                  Upcoming Obligations
                  <ListSectionCount>{upcomingObligations.length}</ListSectionCount>
                  <ChevronRightIcon style={{ transform: obligationsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                </ClickableHeader>
                {obligationsOpen && obligationGroups.flatMap(g => {
                  const groupOpen = openObligationGroups.has(g.name)
                  const rows = [
                    <GroupRow key={`g-${g.name}`} onClick={() => toggleObligationGroup(g.name)}>
                      <ListTaskIcon $recurring><AutorenewIcon /></ListTaskIcon>
                      <GroupName>{g.name}</GroupName>
                      <GroupMeta>
                        <GroupNextDue>Next due {moment(g.earliest).format('MMM D')}</GroupNextDue>
                        <ForecastTag>Forecast</ForecastTag>
                        <GroupCount>{g.items.length}</GroupCount>
                        <ChevronRightIcon style={{ fontSize: 20, color: '#94a3b8', transform: groupOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                      </GroupMeta>
                    </GroupRow>,
                  ]
                  if (groupOpen) {
                    g.items.forEach((o, i) => rows.push(
                      <SubRow
                        key={`s-${g.name}-${o.obligationId}-${i}`}
                        onClick={o.bpId ? () => redirect(PRIVATE_PATHS.compliance.replace(':bpId', o.bpId)) : undefined}
                      >
                        <SubName>{o.bpName || 'Building Passport'}</SubName>
                        <SubDate>{moment(o.date).format('MMM D, YYYY')}</SubDate>
                      </SubRow>
                    ))
                  }
                  return rows
                })}
              </ListSection>
            )}
            <ListParallelSections>
            {hasTaskManagement && (
              <ListSection>
                <ListSectionHeader>
                  <EventIcon />
                  {activeFilter === 'overdue_tasks' ? 'Overdue tasks' : `Tasks in ${currentDate.format('MMMM YYYY')}`}
                  <ListSectionCount>{(activeFilter === 'overdue_tasks' ? overdueTasks : monthTasks).length}</ListSectionCount>
                </ListSectionHeader>
                {(activeFilter === 'overdue_tasks' ? overdueTasks : monthTasks).length === 0 ? (
                  <EmptyMessage>{activeFilter === 'overdue_tasks' ? 'No overdue tasks' : 'No tasks this month'}</EmptyMessage>
                ) : (
                  (activeFilter === 'overdue_tasks' ? overdueTasks : monthTasks).map((task, i) => {
                    const displayStatus = task.isOverdue && task.status !== 'completed' ? 'overdue' : task.status
                    const statusInfo = taskStatusMeta(displayStatus)
                    return (
                      <ListTaskItem key={`${task.id}-${i}`} onClick={() => handleTaskClick(task.id)}>
                        <ListTaskIcon $recurring={task.occurrenceType === 'recurring'}>
                          {task.occurrenceType === 'recurring' ? <RepeatIcon /> : <AssignmentIcon />}
                        </ListTaskIcon>
                        <ListTaskInfo>
                          <ListTaskTitle>{task.title}</ListTaskTitle>
                          <ListTaskMeta>
                            <TaskDate $overdue={task.isOverdue}>
                              {(task.displayDate || moment(task.deadline || task.recurrenceStartDate)).format('ddd, MMM D, YYYY')}
                            </TaskDate>
                            <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
                              {statusInfo.label}
                            </StatusBadge>
                          </ListTaskMeta>
                          {(task.building || task.team) && (
                            <ListTaskMeta>
                              {task.building && <TaskBuilding>{task.building.name}</TaskBuilding>}
                              {task.team && <TaskTeam>{task.team.name}</TaskTeam>}
                            </ListTaskMeta>
                          )}
                        </ListTaskInfo>
                        <TaskPeopleAvatars task={task} max={3} />
                        {task.isOverdue && (
                          <OverdueIcon>
                            <WarningAmberIcon />
                          </OverdueIcon>
                        )}
                      </ListTaskItem>
                    )
                  })
                )}
              </ListSection>
            )}

            <ListSection>
              <ListSectionHeader>
                <InsertDriveFileIcon />
                {activeFilter === 'expired_docs' ? 'Documents that may need renewal' : activeFilter === 'renewed_docs' ? 'Renewed documents' : `Documents in ${currentDate.format('MMMM YYYY')}`}
                <ListSectionCount>{activeFilter === 'expired_docs' ? expiredFileMeta.total : activeFilter === 'renewed_docs' ? renewedFileMeta.total : (fileMeta.total || expiringFiles.length)}</ListSectionCount>
              </ListSectionHeader>
              {(activeFilter === 'expired_docs' ? expiredFiles : activeFilter === 'renewed_docs' ? renewedFiles : expiringFiles).length === 0 ? (
                <EmptyMessage>{activeFilter === 'expired_docs' ? 'All documents are up to date' : activeFilter === 'renewed_docs' ? 'No renewed documents' : 'No documents this month'}</EmptyMessage>
              ) : (
                (activeFilter === 'expired_docs' ? expiredFiles : activeFilter === 'renewed_docs' ? renewedFiles : expiringFiles).map((file, i) => (
                  <ListTaskItem key={file.id || i} onClick={() => handleFileClick(file)}>
                    <ListFileThumbnail>
                      {file.thumbnailUrl ? (
                        <img src={file.thumbnailUrl} alt="" />
                      ) : (
                        <InsertDriveFileIcon style={{ color: '#9ca3af', fontSize: 24 }} />
                      )}
                    </ListFileThumbnail>
                    <ListTaskInfo>
                      <ListTaskTitle>{file.displayName}</ListTaskTitle>
                      {file.fileType && file.fileType !== 'Unknown' && (
                        <FileCardType>{file.fileType}</FileCardType>
                      )}
                      <DateTags>
                        {file.expirationDate && (
                          <DateTag $overdue={moment(file.expirationDate).isBefore(moment())} $color="#0891b2" $bg="#cffafe">
                            {moment(file.expirationDate).isBefore(moment()) ? 'Overdue' : 'Due'} {moment(file.expirationDate).format('D MMM YY')}
                          </DateTag>
                        )}
                        {file.nextInspectionDate && (
                          <DateTag $overdue={moment(file.nextInspectionDate).isBefore(moment())} $color="#0891b2" $bg="#cffafe">
                            Review {moment(file.nextInspectionDate).format('D MMM YY')}
                          </DateTag>
                        )}
                      </DateTags>
                      {file.buildingPassportName && (
                        <FileCardBuilding>{file.buildingPassportName}</FileCardBuilding>
                      )}
                    </ListTaskInfo>
                  </ListTaskItem>
                ))
              )}
              {fileMeta.lastPage > 1 && (
                <PaginationFooter>
                  <PaginationButton
                    disabled={fileMeta.currentPage <= 1 || loadingFiles}
                    onClick={() => fetchExpiringFiles(1, currentDate)}
                  >
                    <FirstPageIcon />
                  </PaginationButton>
                  <PaginationButton
                    disabled={fileMeta.currentPage <= 1 || loadingFiles}
                    onClick={() => fetchExpiringFiles(fileMeta.currentPage - 1, currentDate)}
                  >
                    <ChevronLeftIcon />
                  </PaginationButton>
                  <PaginationInfo>
                    {loadingFiles ? '...' : `${fileMeta.currentPage} / ${fileMeta.lastPage}`}
                  </PaginationInfo>
                  <PaginationButton
                    disabled={fileMeta.currentPage >= fileMeta.lastPage || loadingFiles}
                    onClick={() => fetchExpiringFiles(fileMeta.currentPage + 1, currentDate)}
                  >
                    <ChevronRightIcon />
                  </PaginationButton>
                  <PaginationButton
                    disabled={fileMeta.currentPage >= fileMeta.lastPage || loadingFiles}
                    onClick={() => fetchExpiringFiles(fileMeta.lastPage, currentDate)}
                  >
                    <LastPageIcon />
                  </PaginationButton>
                </PaginationFooter>
              )}
            </ListSection>
            </ListParallelSections>
            {hasTaskManagement && showRemedials && (
              <ListSection>
                <RemedialsDueSection
                  heading={`Remedials due in ${currentDate.format('MMMM')}`}
                  items={remedialsThisMonth.items}
                  total={remedialsThisMonth.total}
                  loading={remedialFeed.loading}
                  failed={remedialFeed.failed}
                  search=''
                  onOpen={openRemedial}
                  onSeeAll={remedialSeeAll}
                />
              </ListSection>
            )}
          </ListView>
        )}
      </ContentArea>
      <GuruInsightModal
        open={showInsightModal}
        onClose={() => { setShowInsightModal(false); setInsightFile(null) }}
        onUpdate={() => {
          fetchExpiringFiles(fileMeta.currentPage, currentDate)
          fetchExpiredFiles(1)
          fetchRenewedFiles(1)
          getExpiryDateCounts('all', bpFilter, selectedFileTypes)
            .then(res => {
              const counts = {}
              ;(res.data.data || []).forEach(item => { counts[item.expiryDate] = item.fileCount })
              setExpiryDateCounts(counts)
            })
            .catch(() => {})
        }}
        file={insightFile}
        bpId={insightFile?.buildingPassportId}
        buildingPassportName={insightFile?.buildingPassportName}
      />
    </PageWrapper>
  )
}

export default TaskCalendar

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`


// Styled Components
const BackToBpBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0 0 6px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  color: #2aa6b3;
  &:hover { text-decoration: underline; }
`

const PageWrapper = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  left: ${({ theme }) => theme.dimensions.navWidth};
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow-y: auto;
  overflow-x: hidden;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    top: 60px;
    bottom: 0;
    /* left/right already fix the width; 100vw counts the scrollbar and
       overflows by its width. */
    width: auto;
    max-width: 100%;
  }
`

const Header = styled.div`
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 18px 24px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 14px 16px 12px;
    gap: 10px;
  }
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  color: #0C1229;

  @media (max-width: 768px) { font-size: 19px; }
`

const AttentionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;

  &:empty { display: none; }

  @media (max-width: 768px) {
    order: 3;
    flex-basis: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`

const AttentionChip = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  border: 1px solid ${({ $level }) => RAG[$level].fg}33;
  background: ${({ $level, $active }) => ($active ? RAG[$level].fg : RAG[$level].bg)};
  color: ${({ $level, $active }) => ($active ? '#fff' : RAG[$level].fg)};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover { border-color: ${({ $level }) => RAG[$level].fg}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }

  @media (max-width: 768px) { min-height: 36px; }
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`


const PageTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  svg {
    font-size: 24px;
    color: #0C1229;
    flex-shrink: 0;
  }

  @media (max-width: 768px) { svg { font-size: 20px; } }
`

const ViewToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 22px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`

const ToggleButton = styled.button`
  min-height: 34px;
  padding: 0 16px;
  border: none;
  border-radius: 18px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#0C1229' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#344054')};
  transition: background 0.15s, color 0.15s;

  &:hover { color: ${({ $active }) => ($active ? '#fff' : '#0C1229')}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }

  @media (max-width: 768px) { min-height: 38px; padding: 0 14px; }
`

const RemedialToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${({ 'aria-pressed': pressed }) => (pressed ? '#33becc' : '#e5e7eb')};
  border-radius: 22px;
  background: ${({ 'aria-pressed': pressed }) => (pressed ? '#e6f7f9' : '#fff')};
  color: #0C1229;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  svg { font-size: 18px; color: ${({ 'aria-pressed': pressed }) => (pressed ? '#2aa6b3' : '#667085')}; }
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const ContentArea = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: 640px) {
    padding: 16px;
    max-width: 100%;
  }

  @media (max-width: 400px) {
    padding: 12px 8px;
  }
`






const CalendarLayout = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasTaskManagement }) => $hasTaskManagement ? 'minmax(0, 1fr) 280px 280px' : 'minmax(0, 1fr) 340px'};
  gap: 20px;
  align-items: stretch;
  min-width: 0;

  & > * {
    min-width: 0;
  }

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) 240px;
    & > :last-child {
      grid-column: 2;
    }
  }

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    gap: 16px;

    & > * {
      grid-column: auto !important;
      grid-row: auto !important;
      width: 100%;
    }
  }
`

const CalendarMain = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  align-self: start;

  @media (max-width: 640px) {
    padding: 12px;
  }

  @media (max-width: 400px) {
    padding: 10px 6px;
  }
`

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
  }
`

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const MonthTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  min-width: 180px;
  text-align: center;

  @media (max-width: 640px) {
    font-size: 16px;
    min-width: 0;
    flex: 1;
  }
`

const CalendarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-end;
  }
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.linen || '#fbede9'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`

const TodayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.15s;

  svg { font-size: 18px; }

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`


const CalendarGrid = styled.div``

const WeekdayHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  margin-bottom: 8px;
`

const WeekdayCell = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  padding: 8px 0;
  text-transform: uppercase;

  @media (max-width: 640px) {
    font-size: 10px;
    padding: 4px 0;
  }
`

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
`

const DayCell = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  aspect-ratio: 1;
  min-height: 60px;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $isSelected, $isToday, theme }) =>
    $isSelected ? (theme.colors.linen || '#fbede9') :
    $isToday ? '#f0f9ff' : 'transparent'};
  border: 2px solid ${({ $isSelected, $isToday, $hasOverdue, theme }) =>
    $isSelected ? theme.colors.primary :
    $hasOverdue ? '#fca5a5' :
    $isToday ? '#bae6fd' : 'transparent'};
  opacity: ${({ $isCurrentMonth }) => $isCurrentMonth ? 1 : 0.4};
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }

  @media (max-width: 640px) {
    /* A square cell would be ~40px tall here; a fixed minimum keeps the tap
       target usable instead. */
    aspect-ratio: auto;
    min-height: 44px;
    padding: 3px;
  }
`

const DayNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 13px;
  font-weight: ${({ $isToday }) => $isToday ? 600 : 400};
  color: ${({ $isToday, theme }) => $isToday ? theme.colors.secondary : theme.colors.haiti};
  background: ${({ $isToday, theme }) => $isToday ? theme.colors.secondary : 'transparent'};
  color: ${({ $isToday }) => $isToday ? 'white' : 'inherit'};
  border-radius: 50%;

  @media (max-width: 640px) {
    width: 20px;
    height: 20px;
    font-size: 12px;
  }
`

const TaskIndicators = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 3px;
  margin-top: 4px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    justify-content: center;
    gap: 2px;
    margin-top: 3px;
  }
`

const TaskDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

const MoreIndicator = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  margin-left: 2px;
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 640px) {
    gap: 10px 14px;
    margin-top: 14px;
    padding-top: 12px;
  }
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const Panel = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`

const SidebarHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const SidebarTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px 0;
`

const TaskCount = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
`

const PanelSearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-bottom: 1px solid #f3f4f6;
`

const PanelSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: #111827;
  background: transparent;
  font-family: inherit;

  &::placeholder { color: #d1d5db; }
`

const PanelSearchClear = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;

  &:hover { color: #6b7280; }
`

const TaskList = styled.div`
  max-height: 500px;
  overflow-y: auto;
`

const EmptyMessage = styled.div`
  padding: 24px 20px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
`

const ObligationSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${({ $clickable }) => ($clickable ? '12px 20px' : '12px 20px 2px')};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0e7490;
  ${({ $clickable }) => $clickable && 'cursor: pointer; user-select: none;'}

  &:hover { ${({ $clickable, theme }) => $clickable && `background: ${theme.colors.lightGrey};`} }

  svg { color: #0891b2; }
`

const SectionCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
`

const SectionHint = styled.div`
  padding: 0 20px 8px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#94a3b8'};
`

const ForecastTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #0e7490;
  background: transparent;
  border: 1px dashed #a3d9e0;
  border-radius: 4px;
  padding: 0 6px;
  line-height: 1.5;
`

const ObligationList = styled.div`
  max-height: 340px;
  overflow-y: auto;
`

const TaskItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  min-width: 0;
  cursor: pointer;

  @media (max-width: 640px) { padding: 12px 14px; gap: 10px; }
  transition: background 0.15s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  ${({ $domainColour }) => $domainColour && `border-left: 3px solid ${$domainColour};`}

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${({ $colour }) => $colour || '#475569'};
  background-color: ${({ $colour }) => `${$colour || '#64748b'}14`};
`

const DomainSwatch = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $colour }) => $colour || '#64748b'};
`

const TaskIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $color }) => ($color ? `${$color}1a` : '#f5f3ff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: ${({ $color }) => $color || '#7c3aed'};
  }
`

const TaskInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const TaskTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const StatusBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

const TaskBuilding = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
`

const TaskTeam = styled.span`
  font-size: 12px;
  color: #0d7377;
  font-weight: 500;
`

const TaskDate = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $overdue, theme }) => $overdue ? '#dc2626' : theme.colors.haiti};
`

const PaginationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.15s;

  svg { font-size: 18px; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.lightGrey};
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`

const PaginationInfo = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  min-width: 40px;
  text-align: center;
`

// List View Styles
const ListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ListParallelSections = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const ListSection = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  overflow: hidden;
`

const ListSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.lightGrey};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  }
`

const ListSectionCount = styled.span`
  margin-left: auto;
  padding: 2px 10px;
  background: white;
  border-radius: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const ListTaskItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const ListTaskIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f5f3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
    color: #7c3aed;
  }
`

const ListTaskInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ListTaskTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const ListTaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`

const RecurrenceInfo = styled.span`
  font-size: 13px;
  color: #7c3aed;
  font-weight: 500;
`

const ClickableHeader = styled(ListSectionHeader)`
  cursor: pointer;
  user-select: none;

  &:hover { background: ${({ theme }) => theme.colors.grey?.[200] || '#eef0f2'}; }
`

const GroupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const GroupName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const GroupMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`

const GroupNextDue = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#94a3b8'};

  @media (max-width: 640px) { display: none; }
`

const GroupCount = styled.span`
  min-width: 26px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  padding: 1px 8px;
`

const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 20px 8px 56px;
  cursor: pointer;
  background: #fbfcfe;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.15s;

  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const SubName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SubDate = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#94a3b8'};
`

const OverdueIcon = styled.div`
  color: #dc2626;
  flex-shrink: 0;

  svg {
    font-size: 24px;
  }
`

const FileIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #cffafe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: #0891b2;
  }
`

const FileExpiryBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #0891b2;
  background: #cffafe;
`

const ListFileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #cffafe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
    color: #0891b2;
  }
`

const DateTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`

const DateTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  color: ${({ $overdue, $color }) => $overdue ? '#ca8a04' : $color};
  background: ${({ $overdue, $bg }) => $overdue ? '#fefce8' : $bg};
  ${({ $overdue }) => $overdue && 'text-decoration: line-through;'}
`

const FileCard = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
  position: relative;
  opacity: ${({ $renewed }) => $renewed ? 0.6 : 1};

  &:hover {
    background: #f9fafb;
    opacity: 1;
  }

  &:last-child {
    border-bottom: none;
  }
`

const FileCardUrgentBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #dc2626;
  border-radius: 3px 0 0 3px;
`

const FileThumbnail = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FileCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const FileCardName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $renewed }) => $renewed ? '#9ca3af' : '#111827'};
  text-decoration: ${({ $renewed }) => $renewed ? 'line-through' : 'none'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`

const FileCardType = styled.span`
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
`

const FlagWarning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #d97706;
  cursor: help;
`

const FileCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
`

const RenewalBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #7c3aed;
  background: #f5f3ff;
  padding: 1px 6px;
  border-radius: 4px;
`

const RenewedBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #059669;
  background: #ecfdf5;
  padding: 1px 6px;
  border-radius: 4px;
`

const NeedsActionBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #dc2626;
  background: #fef2f2;
  padding: 1px 6px;
  border-radius: 4px;
`

const FileStatusMessage = styled.div`
  font-size: 11px;
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: 6px;
  margin-top: 4px;
  ${({ $type }) => $type === 'urgent' && `
    color: #991b1b;
    background: #fef2f2;
    border-left: 3px solid #dc2626;
  `}
  ${({ $type }) => $type === 'warning' && `
    color: #92400e;
    background: #fffbeb;
    border-left: 3px solid #d97706;
  `}
  ${({ $type }) => $type === 'ok' && `
    color: #065f46;
    background: #ecfdf5;
    border-left: 3px solid #059669;
  `}
  ${({ $type }) => $type === 'info' && `
    color: #374151;
    background: #f9fafb;
  `}
`

const FileCardBuilding = styled.span`
  font-size: 11px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ListFileThumbnail = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`


const DayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  /* A day cell is ~40px wide on a phone, so "3 tasks" only ever renders as
     "3 t…". Collapse the badge into a dot. The day's list is one tap away. */
  @media (max-width: 640px) {
    width: 7px;
    height: 7px;
    padding: 0;
    border-radius: 50%;
    font-size: 0;
    line-height: 0;
  }
`

const ForecastBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 600;
  color: #0e7490;
  background: transparent;
  border: 1px dashed #a3d9e0;
  border-radius: 4px;
  padding: 0 5px;
  line-height: 1.4;
  white-space: nowrap;
`

const ForecastDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px dashed #67c6d1;
  background: #e6f7f9;
`

const BpFilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  padding: 3px 10px;
  background: #e6f7f9;
  border: 1px solid #bfdbfe;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #33becc;
`

const BpFilterClear = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;

  &:hover {
    color: #dc2626;
  }
`


const FileTypeFilterWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  /* Static on mobile so the dropdown below positions against the sticky
     header and can span its full width instead of the trigger's. */
  @media (max-width: 768px) { position: static; }
`

const FileTypeDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  min-width: 320px;
  max-height: 400px;

  @media (max-width: 768px) {
    left: 12px;
    right: 12px;
    min-width: 0;
    width: auto;
    max-height: 60vh;
  }
  background: white;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  margin-top: 4px;
  padding: 8px;
`

const SyncWrapper = styled.div`
  position: relative;
`

const SyncScopeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f0;
`
const SyncScopeLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
`
const SyncButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  white-space: nowrap;
  border: 1px solid ${({ $hasSelection }) => ($hasSelection ? '#33becc' : '#e5e7eb')};
  border-radius: 22px;
  background: ${({ $hasSelection }) => ($hasSelection ? '#e6f7f9' : '#fff')};
  color: #0C1229;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  svg { color: #667085; }
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const ClearFilterInline = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(124, 58, 237, 0.2);
  margin-left: 4px;
  flex-shrink: 0;

  &:hover {
    background: rgba(124, 58, 237, 0.4);
  }
`

const SyncDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 9999;

  @media (max-width: 768px) {
    min-width: 0;
    width: min(260px, calc(100vw - 32px));
    max-height: 60vh;
  }
`

const SyncOption = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f3f4f6;
  }
`

const SyncNote = styled.div`
  font-size: 12px;
  color: #374151;
  padding: 10px 12px;
  line-height: 1.5;
`

const SyncStats = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 6px 0;
`

const SyncStatsClickable = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #7c3aed;
  margin: 6px 0;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #6d28d9;
  }
`

const SyncBreakdown = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 6px 0 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SyncBreakdownItem = styled.div`
  font-size: 12px;
  color: ${({ $muted }) => $muted ? '#6b7280' : '#374151'};
  font-style: ${({ $muted }) => $muted ? 'italic' : 'normal'};

  span {
    color: #7c3aed;
    margin-right: 6px;
  }
`

const SyncDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
`

const PanelTabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const PanelTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 10px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 2px solid ${({ 'aria-selected': selected }) => (selected ? '#0C1229' : 'transparent')};
  background: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ 'aria-selected': selected }) => (selected ? '#0C1229' : '#667085')};
  cursor: pointer;

  svg { font-size: 18px; }
  &:hover { color: #0C1229; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: -2px; }
`

const PanelTabCount = styled.span`
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f2f4f7;
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  text-align: center;
`

const ListCountLine = styled.div`
  padding: 10px 20px;
  border-top: 1px solid #f2f4f7;
  font-size: 13px;
  color: #475467;
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  min-width: 0;
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
`

const PanelSkeleton = styled.div`
  padding: 12px 20px;
`
